import React from 'react';
import { Col, Row } from '../../components/RowCol';
import Spacer from '../../components/Spacer';
import { Category } from '../Game/Game';
import CategorySquare from '../../components/BigBoard/CategorySquare';
import DollarSquare from '../../components/BigBoard/DollarSquare';
import { EditFunction } from './GameEditor';
import TextEditor from '../../components/TextEditor';
import ConfirmableButton from '../../components/ConfirmableButton';
import Button from '../../components/Button';
import QuestionEditor from './QuestionEditor';
import BigQuestion from '../Game/BigQuestion';
import LocalFileStorage from '../../LocalFileStorage';

type Props = {
	categories: Category[];
	levels: number[];
	editRound: EditFunction;
	localFileStorage: LocalFileStorage;
}

type State = {
	editingCategoryIndex: number | null;
	previewQuestionIndex: number | null;
}

class BoardEditor extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);

		this.state = {
			editingCategoryIndex: null,
			previewQuestionIndex: null,
		};
	}

	editCategory = (categoryIndex: number | null) => {
		this.setState({
			editingCategoryIndex: categoryIndex,
			previewQuestionIndex: null,
		});
	};

	previewQuestion = (previewQuestionIndex: number) => {
		this.setState({
			previewQuestionIndex,
		});
	};

	editQuestion = (categoryIndex: number, questionIndex: number) =>
		(
			editPath: (string | number)[],
			editType: 'edit' | 'insert_element' | 'delete_element' | 'delete_field',
			value?: string | number,
		) => {
			const { editRound } = this.props;
			editRound(
				['categories', categoryIndex, 'questions', questionIndex].concat(editPath),
				editType,
				value,
			);
		};

	addCategory = (newCategoryIndex: number) => {
		const { levels, editRound } = this.props;
		const questions = Array(levels.length).fill(null).map(() => ({
			type: 'text',
			text: '',
			answer: '',
		}));

		editRound(
			['categories', newCategoryIndex],
			'insert_element',
			{
				name: '',
				author: '',
				questions,
			},
		);

		this.editCategory(newCategoryIndex);
	};

	render() {
		const { categories, levels, editRound, localFileStorage } = this.props;
		const { editingCategoryIndex, previewQuestionIndex } = this.state;

		if (editingCategoryIndex !== null) {
			const category = categories[editingCategoryIndex];
			const categoryIndex = editingCategoryIndex;
			return (
				<Row>
					<Col>
						<Row alignCenter style={{
							position: 'relative',
						}}>
							<CategorySquare
								name={category.name}
								author={category.author}
							/>
							<Spacer horizontal={20} />
							<Col>
								<TextEditor
									placeholder="Category"
									text={category.name}
									autoFocus
									edit={name => {
										editRound(
											['categories', categoryIndex, 'name'],
											'edit',
											name,
										);
									}}
								/>
								<Spacer vertical={8} />
								<TextEditor
									placeholder="Author"
									text={category.author ?? ''}
									edit={author => {
										if (author.length > 0) {
											editRound(
												['categories', categoryIndex, 'author'],
												'edit',
												author,
											);
										} else {
											editRound(
												['categories', categoryIndex, 'author'],
												'delete_field',
											);
										}
									}}
								/>
								<Spacer vertical={8} />
								<ConfirmableButton
									label="Delete this category"
									onClick={() => {
										editRound(
											['categories', categoryIndex],
											'delete_element',
										);
										this.editCategory(null);
									}}
									small
								/>
							</Col>
							<Spacer horizontal={20} />
							<Col justifyStart style={{
								alignSelf: 'flex-start',
								alignItems: 'flex-end',
								width: '200px',
							}}>
								<Spacer vertical={8} />
								<Button
									small
									label="Done"
									onClick={() => this.editCategory(null)}
								/>
							</Col>
						</Row>

						<Spacer vertical={15} />

						{category.questions.map((question, questionIndex) => (
							<React.Fragment key={questionIndex}>
								<Row
									alignCenter
									onClick={() => this.previewQuestion(questionIndex)}
								>
									<DollarSquare
										level={levels[questionIndex]}
										isOpen={question.text.length === 0}
										className="hoverable"
										onClick={() => undefined}
										dd={question.dailyDouble}
									/>
									<Spacer horizontal={20} />
									<QuestionEditor
										question={question}
										edit={this.editQuestion(categoryIndex, questionIndex)}
										allowDailyDouble
										localFileStorage={localFileStorage}
									/>
								</Row>
								{questionIndex < levels.length - 1 && (
									<Spacer vertical={10} />
								)}
							</React.Fragment>
						))}
					</Col>

					<Spacer horizontal={100} />

					{previewQuestionIndex !== null && (
						<div style={{
							width: '1020px',
							height: '829px',
						}}>
							<BigQuestion
								question={category.questions[previewQuestionIndex]}
								dailyDoubleWager={0}
								finalJeopardy={false}
								finalJeopardyWagers={null}
								localFileStorage={localFileStorage}
							/>
						</div>
					)}
				</Row>
			);
		}

		return (
			<Row>
				{categories.map((category, categoryIndex) => (
					<React.Fragment
						key={categoryIndex}
					>
						<Col
							className="hoverable"
							onClick={() => this.editCategory(categoryIndex)}
						>
							<CategorySquare
								name={category.name}
								author={category.author}
							/>
							<Spacer vertical={15} />
							{levels.map((level, levelIndex) => (
								<React.Fragment
									key={level}
								>
									<DollarSquare
										level={level}
										isOpen={category.questions[levelIndex].text.length === 0}
										onClick={() => undefined}
										dd={category.questions[levelIndex].dailyDouble}
									/>
									{levelIndex < levels.length - 1 && (
										<Spacer vertical={10} />
									)}
								</React.Fragment>
							))}
						</Col>
						<Spacer horizontal={10} />
					</React.Fragment>
				))}

				<>
					<Col
						center
						className="hoverable"
						style={{
							width: '196px',
							fontFamily: 'HelveticaInserat',
							textTransform: 'uppercase',
							fontSize: '24px',
							textAlign: 'center',
						}}
						onClick={() => this.addCategory(categories.length)}
					>
						<div>
							+
						</div>
						<Spacer vertical={10} />
						<div>
							Add Category
						</div>
					</Col>
				</>
			</Row>
		);
	}
}

export default BoardEditor;
